import React from 'react'
import { Box, Alert } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import {
  GamerFeedbackBackButton,
  IndividualProfileSection,
  ExperimentalSectionCard,
  FeedbackEmptyState
} from '../../feedbackGenerator/components'
import { INTERAX_COLORS } from '../../../constants/colors'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { useSwimmerIndividualDataForGamer } from '../../../apiLayer/queryHooks/queries'
import { UserIcon, SparklesIcon, LightBulbIcon, TrophyIcon } from '@heroicons/react/24/solid'
import Spinner from '../../../interaxDesignSystemComponents/Spinner'
import { normalizeProfileVariable } from '../../feedbackGenerator/utils/profileDescriptionGenerator'
import { PageWrapper } from '../../../shared/components'
import { GlowCursorProvider } from '../components/GlowCursor'
import { SparkleCursor } from '../components/SparkleCursor'
import { DEEPER_INDIVIDUAL_ANALYSIS_EXPERIENCE_KEY } from '../constants/storageKeys'
import type { ImprovementAreaItem, StrengthItem } from '../../../types/TeamSwimmerFeedbackTypes'
import type { SectionDataRecord } from '../../feedbackGenerator/components'

/**
 * DeeperIndividualAnalysisPage - Data-driven deeper analysis view for individual feedback
 *
 * Uses swimmer data format with array-based tab structure
 * Single source of truth: useSwimmerIndividualDataForGamer
 */
const getCustomCursorStyles = (isActive: boolean) =>
  isActive
    ? {
        cursor: 'url(/assets/svg/mouseCursor.svg) 16 16, auto'
      }
    : {}

export const DeeperIndividualAnalysisPage: React.FC = () => {
  const [showFirstVisitExperience, setShowFirstVisitExperience] = React.useState(false)
  const [pendingSections, setPendingSections] = React.useState<string[]>([])
  const [hasInitialGlowDelayElapsed, setHasInitialGlowDelayElapsed] = React.useState(false)
  const initialPendingSectionsRef = React.useRef<string[]>([])
  const pendingInitializedRef = React.useRef(false)
  const glowDelayTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const [glintActivity, setGlintActivity] = React.useState<Record<string, boolean>>({})
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Fetch swimmer data (THE ONLY data source)
  const { data: swimmerResponse, isLoading, error } = useSwimmerIndividualDataForGamer()

  // Extract swimmer data (data IS the individualSessionFeedbackSwimmer)
  const feedbackData = swimmerResponse?.data as any

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const hasSeenExperience = window.localStorage.getItem(DEEPER_INDIVIDUAL_ANALYSIS_EXPERIENCE_KEY)
    if (!hasSeenExperience) {
      setShowFirstVisitExperience(true)
      window.localStorage.setItem(DEEPER_INDIVIDUAL_ANALYSIS_EXPERIENCE_KEY, 'true')
    }
  }, [])

  // 🚨 COMPREHENSIVE CONSOLE LOGGING FOR DATA INSPECTION 🚨

  if (feedbackData) {
    Object.keys(feedbackData).forEach((key) => {
      if (Array.isArray(feedbackData[key])) {
      } else {
      }
    })
  }

  // Extract session profile data (first element of array)
  const sessionProfile = feedbackData?.sessionProfile?.[0]

  // Extract experience data
  const experienceData = feedbackData?.experience || []

  // Transform experience array to object format for ExperimentalSectionCard
  const experienceTabsData: Record<string, any> = {}
  experienceData.forEach((item: any, index: number) => {
    if (index === 0) {
      // Emotion tab
      experienceTabsData.emotion = item
    } else if (index === 1) {
      // Psychological Safety tab
      experienceTabsData.psychologicalSafety = item
    }
  })

  // Transform improvement areas array to tabs and data object
  const improvementAreasTabs =
    feedbackData?.improvementAreas?.map((item: ImprovementAreaItem, index: number) => ({
      label: t(`player.weaknesses.${item.variable}.label`, {
        ns: 'feedbackTexts',
        defaultValue: item.variable
      }),
      key: `improvement_${index}`
    })) || []

  const improvementAreasData =
    feedbackData?.improvementAreas?.reduce((acc: SectionDataRecord, item: ImprovementAreaItem, index: number) => {
      acc[`improvement_${index}`] = {
        ...item,
        context: 'weaknesses'
      }
      return acc
    }, {}) || {}

  // Transform strengths array to tabs and data object
  const strengthsTabs =
    feedbackData?.strengths?.map((item: StrengthItem, index: number) => ({
      label: t(`player.strengths.${item.variable}.label`, {
        ns: 'feedbackTexts',
        defaultValue: item.variable
      }),
      key: `strength_${index}`
    })) || []

  const strengthsData =
    feedbackData?.strengths?.reduce((acc: SectionDataRecord, item: StrengthItem, index: number) => {
      acc[`strength_${index}`] = {
        ...item,
        context: 'strengths'
      }
      return acc
    }, {}) || {}

  const renderedSectionIds = React.useMemo(() => {
    if (!feedbackData) return []

    const sections: string[] = []

    if (experienceData.length > 0) sections.push('experience')
    if (feedbackData.improvementAreas && feedbackData.improvementAreas.length > 0) sections.push('improvement')
    if (feedbackData.strengths && feedbackData.strengths.length > 0) sections.push('strengths')

    return sections
  }, [feedbackData, experienceData.length])

  React.useEffect(() => {
    if (!showFirstVisitExperience) {
      if (pendingSections.length > 0) {
        setPendingSections([])
      }
      initialPendingSectionsRef.current = []
      pendingInitializedRef.current = false
      return
    }

    if (renderedSectionIds.length === 0) {
      if (pendingSections.length > 0) {
        setPendingSections([])
      }
      initialPendingSectionsRef.current = []
      pendingInitializedRef.current = false
      return
    }

    setPendingSections((prev) => {
      const filteredPrev = prev.filter((id) => renderedSectionIds.includes(id))

      if (!pendingInitializedRef.current) {
        pendingInitializedRef.current = true
        initialPendingSectionsRef.current = renderedSectionIds

        return renderedSectionIds
      }

      const knownSections = initialPendingSectionsRef.current
      const newSections = renderedSectionIds.filter((id) => !knownSections.includes(id))

      if (newSections.length > 0) {
        initialPendingSectionsRef.current = [...knownSections, ...newSections]

        return [...filteredPrev, ...newSections]
      }

      if (filteredPrev.length !== prev.length) {
        return filteredPrev
      }

      return prev
    })
  }, [showFirstVisitExperience, renderedSectionIds, pendingSections.length])

  React.useEffect(() => {
    if (showFirstVisitExperience && pendingInitializedRef.current && pendingSections.length === 0) {
      pendingInitializedRef.current = false
      setShowFirstVisitExperience(false)
    }
  }, [showFirstVisitExperience, pendingSections.length])

  React.useEffect(() => {
    if (!showFirstVisitExperience) {
      setHasInitialGlowDelayElapsed(false)
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
      return
    }

    if (isLoading || error || !feedbackData || pendingSections.length === 0) {
      setHasInitialGlowDelayElapsed(false)
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
      return
    }

    if (hasInitialGlowDelayElapsed || glowDelayTimeoutRef.current) return

    glowDelayTimeoutRef.current = setTimeout(() => {
      setHasInitialGlowDelayElapsed(true)
      glowDelayTimeoutRef.current = null
    }, 300)

    return () => {
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
    }
  }, [showFirstVisitExperience, isLoading, error, feedbackData, pendingSections.length, hasInitialGlowDelayElapsed])

  React.useEffect(
    () => () => {
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
    },
    []
  )

  const isGlowActive =
    showFirstVisitExperience &&
    pendingSections.length > 0 &&
    !isLoading &&
    !error &&
    Boolean(feedbackData) &&
    hasInitialGlowDelayElapsed

  const isGlintActive = React.useMemo(() => Object.values(glintActivity).some(Boolean), [glintActivity])

  const handleSectionGlintStateChange = React.useCallback((sectionId: string, state: 'idle' | 'running') => {
    setGlintActivity((prev) => {
      const nextState = state === 'running'
      const currentState = prev[sectionId] ?? false
      if (currentState === nextState) {
        return prev
      }
      if (!nextState) {
        const { [sectionId]: _, ...rest } = prev
        return rest
      }
      return {
        ...prev,
        [sectionId]: true
      }
    })
  }, [])

  const handleSectionReveal = React.useCallback((sectionId: string) => {
    setPendingSections((prev) => {
      if (!prev.includes(sectionId)) return prev
      const next = prev.filter((id) => id !== sectionId)

      return next
    })
  }, [])

  const cursorStyles = getCustomCursorStyles(isGlowActive)

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: INTERAX_COLORS.offBlack,
          ...getCustomCursorStyles(false)
        }}
      >
        <Spinner size="large" color="sun" />
      </PageWrapper>
    )
  }

  // Error state
  if (error) {
    return (
      <PageWrapper sx={{ backgroundColor: INTERAX_COLORS.offBlack, ...getCustomCursorStyles(false) }}>
        <Alert severity="error">Failed to load individual feedback data</Alert>
      </PageWrapper>
    )
  }

  // No data state
  if (!feedbackData) {
    return (
      <PageWrapper sx={{ backgroundColor: INTERAX_COLORS.offBlack, ...getCustomCursorStyles(false) }}>
        <GamerFeedbackBackButton
          label={t('gamer.feedback.deeperAnalysis.backToFeedback')}
          onClick={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}
        />
        <Box
          sx={{
            marginTop: '24px',
            background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
            border: `1px solid ${INTERAX_COLORS.white04}`,
            boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white08}`,
            borderRadius: '10px',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FeedbackEmptyState />
        </Box>
      </PageWrapper>
    )
  }

  return (
    <GlowCursorProvider active={isGlowActive} showGlobalGlow>
      <SparkleCursor active={isGlowActive && isGlintActive} />
      <PageWrapper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          backgroundColor: INTERAX_COLORS.offBlack,
          position: 'relative',
          ...cursorStyles
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 20, display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Back Button */}
          <GamerFeedbackBackButton
            label={t('gamer.feedback.deeperAnalysis.backToFeedback')}
            onClick={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}
          />

          {/* Session Profile + Experience bundle */}
          {(sessionProfile || experienceData.length > 0) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                width: '100%',
                '@media (min-width: 1600px)': {
                  flexDirection: 'row'
                }
              }}
            >
              {sessionProfile && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {(() => {
                    const normalizedTopVariable = normalizeProfileVariable(sessionProfile.topStrength.variable)
                    const normalizedSecondVariable = normalizeProfileVariable(sessionProfile.secondStrength.variable)

                    const profileName = `${t(`individual.profile.${normalizedTopVariable}.topStrength`, {
                      ns: 'feedbackTexts',
                      defaultValue: sessionProfile.topStrength.variable
                    })} ${t(`individual.profile.${normalizedSecondVariable}.secondStrength`, {
                      ns: 'feedbackTexts',
                      defaultValue: sessionProfile.secondStrength.variable
                    })}`

                    const description = `${t(`individual.profile.${normalizedTopVariable}.topStrengthText`, {
                      ns: 'feedbackTexts',
                      defaultValue: ''
                    })} ${t(`individual.profile.${normalizedSecondVariable}.secondStrengthText`, {
                      ns: 'feedbackTexts',
                      defaultValue: ''
                    })}`

                    return (
                      <IndividualProfileSection
                        profileName={profileName}
                        description={description}
                        traits={[
                          {
                            name: t(`individual.profile.${normalizedTopVariable}.label`, {
                              ns: 'feedbackTexts',
                              defaultValue: sessionProfile.topStrength.variable
                            }),
                            level: 'excellent',
                            levelLabel: t('gamer.feedback.profile.levelExcellent', 'Excellent')
                          },
                          {
                            name: t(`individual.profile.${normalizedSecondVariable}.label`, {
                              ns: 'feedbackTexts',
                              defaultValue: sessionProfile.secondStrength.variable
                            }),
                            level: 'good',
                            levelLabel: t('gamer.feedback.profile.levelGood', 'Good')
                          }
                        ]}
                        visualization={
                          sessionProfile.visualization?.type === 'quote'
                            ? {
                                text: sessionProfile.visualization.text,
                                speaker: sessionProfile.visualization.speaker
                              }
                            : undefined
                        }
                        headerTitle="Session Profile"
                        headerIcon={<UserIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
                      />
                    )
                  })()}
                </Box>
              )}

              {experienceData.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ExperimentalSectionCard
                    titleKey="gamer.feedback.sections.experience.title"
                    descriptionKey="gamer.feedback.sections.experience.description"
                    tabs={[
                      { label: t('gamer.feedback.sections.experience.tabs.emotion'), key: 'emotion' },
                      {
                        label: t('gamer.feedback.sections.experience.tabs.psychologicalSafety'),
                        key: 'psychologicalSafety'
                      }
                    ]}
                    sectionData={experienceTabsData}
                    variant="individual"
                    icon={<SparklesIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
                    showGlowExperience={pendingSections.includes('experience')}
                    onGlowExperienceComplete={() => handleSectionReveal('experience')}
                    onGlintStateChange={(state) => handleSectionGlintStateChange('experience', state)}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Improvement Areas + Strengths bundle */}
          {((feedbackData?.improvementAreas && feedbackData.improvementAreas.length > 0) ||
            (feedbackData?.strengths && feedbackData.strengths.length > 0)) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                width: '100%',
                '@media (min-width: 1600px)': {
                  flexDirection: 'row'
                }
              }}
            >
              {feedbackData?.improvementAreas && feedbackData.improvementAreas.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ExperimentalSectionCard
                    titleKey="gamer.feedback.sections.improvementAreas.title"
                    descriptionKey="gamer.feedback.sections.improvementAreas.description"
                    tabs={improvementAreasTabs}
                    sectionData={improvementAreasData}
                    variant="individual"
                    icon={<LightBulbIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
                    showGlowExperience={pendingSections.includes('improvement')}
                    onGlowExperienceComplete={() => handleSectionReveal('improvement')}
                    onGlintStateChange={(state) => handleSectionGlintStateChange('improvement', state)}
                  />
                </Box>
              )}

              {feedbackData?.strengths && feedbackData.strengths.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ExperimentalSectionCard
                    titleKey="gamer.feedback.sections.strengths.title"
                    descriptionKey="gamer.feedback.sections.strengths.description"
                    tabs={strengthsTabs}
                    sectionData={strengthsData}
                    variant="individual"
                    icon={<TrophyIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />}
                    showGlowExperience={pendingSections.includes('strengths')}
                    onGlowExperienceComplete={() => handleSectionReveal('strengths')}
                    onGlintStateChange={(state) => handleSectionGlintStateChange('strengths', state)}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </PageWrapper>
    </GlowCursorProvider>
  )
}

export default DeeperIndividualAnalysisPage
